import React from 'react';

import { RichTextarea } from '@dashboard/components/Formik';
import { dataFieldTypes } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import { productTypes } from '@dashboard/enums/product-types';
import { htmlToPlainText } from '@dashboard/lib/htmlToPlainText';

const ConfirmationCheckoutMessageEditor = ({
  id,
  name,
  values,
  page,
  setFieldValue,
  initialValue,
  appearance,
  reminderEmail = false,
}) => {
  const paymentVariables = [
    { name: 'Payment amount', id: 'price' },
    ...(reminderEmail === false
      ? [
          { name: 'Payment amount due', id: 'amountDue' },
          { name: 'Payment amount paid', id: 'amountPaid' },
        ]
      : []),
    { name: 'Payment date', id: 'createdAt' },
    ...(reminderEmail === false
      ? [
          { name: 'Payment status', id: 'status' },
          { name: 'Payment method', id: 'payment_method' },
        ]
      : []),
    { name: 'Order ID', id: 'orderId' },
  ];

  const subscriptionVariables = [
    { name: 'Order ID', id: 'orderId' },
    { name: 'Subscription amount', id: 'price' },
    { name: 'Subscribed date', id: 'createdAt' },
    ...(reminderEmail === false
      ? [
          { name: 'Subscription status', id: 'status' },
          { name: 'Subscription next payment date', id: 'currentPeriodEnd' },
          { name: 'Payment method', id: 'payment_method' },
        ]
      : []),
    ...(page.product?.planIterations ? [{ name: 'No. of payments', id: 'planIterations' }] : []),
  ];

  const productVariables = [
    { name: 'Product title', id: 'product_title' },
    { name: 'Variant selection', id: 'variant_breakdown' },
    ...(page.product?.variants ?? [])
      .map(variant => [
        {
          name: htmlToPlainText(variant.name),
          id: `variant_${variant._id}`,
        },
        ...(variant.options.some(option => option.sku)
          ? [
              {
                name: `${htmlToPlainText(variant.name)} SKU`,
                id: `variant_${variant._id}_sku`,
              },
            ]
          : []),
      ])
      .flat(),
    ...(page.product?.generateLicenseKeys ? [{ name: 'License key', id: 'license_key' }] : []),
    { name: 'SKU', id: 'sku' },
  ];

  const submissionVariables = [
    { name: 'Form title', id: 'form_title' },
    { name: 'Submission date', id: 'createdAt' },
  ];

  const eventVariables = [
    { name: 'Event name', id: 'event_name' },
    { name: 'Event date/time', id: 'event_date' },
    { name: 'Event location', id: 'event_location' },
    { name: 'Tickets purchased', id: 'event_tickets' },
    {
      name: 'Virtual meeting link',
      id: 'event_meeting_link',
    },
  ];

  const genericVariables = [
    ...(reminderEmail === false
      ? [{ name: 'Customer portal link', id: 'customer_portal_link' }]
      : []),
    ...page.fields.map(field => {
      if (!field.name) {
        return {
          name: field.label,
          id: `field_${field._id}`,
        };
      }

      const dataField = dataFieldTypes.find(f => f.name === field.type);

      return {
        name: dataField?.label || field.label,
        id: `field_${field._id}`,
      };
    }),
    ...(page.showCouponCodeField
      ? [
          { name: 'Coupon code', id: 'coupon_code' },
          { name: 'Coupon discount', id: 'coupon_discount' },
        ]
      : []),
  ];

  let variables = [];

  if (page.type === PAGE_TYPES.FORM) {
    variables = [...submissionVariables, ...genericVariables];
  } else if (page.type === PAGE_TYPES.CHECKOUT) {
    if (page.product.type === productTypes.SUBSCRIPTION) {
      variables = [...subscriptionVariables, ...productVariables, ...genericVariables];
    } else if (page.product.type === productTypes.CHARGE) {
      variables = [...paymentVariables, ...productVariables, ...genericVariables];
    }
  } else if (page.type === PAGE_TYPES.EVENT) {
    variables = [...paymentVariables, ...eventVariables, ...genericVariables];
  }

  return (
    <RichTextarea
      id={id}
      name={name}
      values={values}
      setFieldValue={setFieldValue}
      variables={variables}
      initialValue={initialValue}
      appearance={appearance}
    />
  );
};

export default ConfirmationCheckoutMessageEditor;
